import React, { useCallback, useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { errMsg } from "../../components/crm/crmUtil";
import TemplateEditorForm from "../../components/sales/TemplateEditorForm";
import TemplatePreview from "../../components/sales/TemplatePreview";
import TemplateVersions from "../../components/sales/TemplateVersions";
import DocumentBranding from "../../components/sales/DocumentBranding";
import { isTenantAdmin, latestPerKey } from "../../components/sales/templateUtil";

const BLANK = { key: "", name: "", body: "", note: "" };

// B5 part 2c: tenant-managed contract templates (versioned, merge-field picker, server preview). Founder/Admin only.
export default function ContractTemplates() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState("");                 // "" = new template
  const [form, setForm] = useState(BLANK);
  const [msg, setMsg] = useState({ ok: "", err: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/sales/contract-templates").then((r) => setData(r.data)).catch((x) => setMsg({ ok: "", err: errMsg(x) })), []);
  useEffect(() => { if (isTenantAdmin(user)) load(); }, [load, user]);
  if (!isTenantAdmin(user)) {
    return <AppShell title="Contract templates"><p className="px-4 md:px-6 text-sm" role="alert" data-testid="tpl-forbidden">Only the Founder or an Administrator can manage contract templates.</p></AppShell>;
  }
  const rows = data?.templates || [];
  const pickKey = (k) => {
    setSel(k); setMsg({ ok: "", err: "" });
    const t = rows.find((r) => r.key === k);
    setForm(t ? { key: t.key, name: t.name, body: t.body, note: "" } : BLANK);
  };
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post("/sales/contract-templates", { key: form.key, name: form.name.trim(), body: form.body, ...(form.note.trim() && { note: form.note.trim() }) });
      await load(); setSel(r.data.key); setForm({ ...form, note: "" }); setMsg({ ok: `Saved as v${r.data.version}.`, err: "" });
    } catch (x) { setMsg({ ok: "", err: errMsg(x) }); } finally { setBusy(false); }
  };
  return (
    <AppShell title="Contract templates" subtitle="Versioned templates used to generate contracts from accepted proposals">
      <div className="mx-auto w-full max-w-[1200px] px-4 md:px-6 grid gap-4 md:grid-cols-[15rem_minmax(0,1fr)]" data-testid="contract-templates">
        <nav aria-label="Templates" className="space-y-2" data-testid="tpl-list">
          <button type="button" onClick={() => pickKey("")} aria-current={sel === "" ? "true" : undefined}
            className={`w-full text-left min-h-[44px] px-3 rounded-sm border text-sm ${sel === "" ? "border-erf-dark bg-erf-creamSecondary" : "border-erf-borderCream bg-white"}`} data-testid="tpl-new">+ New template</button>
          {latestPerKey(rows).map((t) => (
            <button key={t.key} type="button" onClick={() => pickKey(t.key)} aria-current={sel === t.key ? "true" : undefined}
              className={`w-full text-left min-h-[44px] px-3 py-2 rounded-sm border text-sm break-words ${sel === t.key ? "border-erf-dark bg-erf-creamSecondary" : "border-erf-borderCream bg-white"}`}
              data-testid={`tpl-key-${t.key}`}>{t.name} <span className="text-erf-dark/60">v{t.version}</span></button>
          ))}
          <DocumentBranding />
        </nav>
        <div className="space-y-4 min-w-0">
          {msg.err && <p role="alert" className="text-sm text-red-700" data-testid="tpl-error">{msg.err}</p>}
          {msg.ok && <p role="status" className="text-sm text-green-800" data-testid="tpl-saved">{msg.ok}</p>}
          <div className="grid gap-4 lg:grid-cols-2">
            <TemplateEditorForm form={form} setForm={setForm} isNew={sel === ""} fields={data?.merge_fields || []}
              labels={data?.merge_field_labels} onSave={save} busy={busy} />
            <TemplatePreview body={form.body} />
          </div>
          <TemplateVersions versions={rows.filter((r) => r.key === sel)}
            onLoad={(v) => { setForm({ key: v.key, name: v.name, body: v.body, note: `Based on v${v.version}` }); setMsg({ ok: `Loaded v${v.version} into the editor.`, err: "" }); }} />
        </div>
      </div>
    </AppShell>
  );
}
